import { useCallback } from "react"
import { AxiosResponse } from "axios"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import {
  getComments,
  addComment,
  updateCommentApi,
  deleteCommentApi,
  resolveCommentApi,
  addReplyApi,
  deleteReplyApi,
} from "api/Repos/comments"
import { Comment, CommentReply, CommentsStorageData } from "types/comments.types"

/**
 * Imperative comment API functions, built directly on `useAuthedAxios`.
 *
 * Deliberately NOT react-query: comments are collaborative state owned by
 * `commentsStore` via `ApiStorageProvider` (optionally synced over Yjs
 * WebSockets). A react-query cache in the middle would create a second owner
 * for the same data. This hook only supplies the transport functions the
 * provider is constructed with.
 */

interface DeleteCommentData {
  deletedBy: string
  deletedByName: string
  reason: string
}

interface UseCommentsApiReturn {
  getCommentsById: (
    id: string,
    owner: string
  ) => Promise<AxiosResponse<CommentsStorageData>>

  addCommentById: (
    id: string,
    owner: string,
    comment: Comment
  ) => Promise<AxiosResponse<{ id: string }>>

  updateCommentById: (
    id: string,
    owner: string,
    commentId: string,
    updates: Partial<Comment>
  ) => Promise<AxiosResponse<{ success: boolean }>>

  deleteCommentById: (
    id: string,
    owner: string,
    commentId: string,
    deletionData: DeleteCommentData
  ) => Promise<AxiosResponse<{ success: boolean }>>

  resolveCommentById: (
    id: string,
    owner: string,
    commentId: string,
    resolvedBy: string,
    resolvedByName: string
  ) => Promise<AxiosResponse<{ success: boolean }>>

  addReplyById: (
    id: string,
    owner: string,
    commentId: string,
    reply: CommentReply
  ) => Promise<AxiosResponse<{ id: string }>>

  deleteReplyById: (
    id: string,
    owner: string,
    commentId: string,
    replyId: string
  ) => Promise<AxiosResponse<{ success: boolean }>>
}

export const useCommentsApi = (): UseCommentsApiReturn => {
  const getAxios = useAuthedAxios()

  const getCommentsById = useCallback(
    async (id: string, owner: string) =>
      getComments(await getAxios(), id, owner),
    [getAxios]
  )

  const addCommentById = useCallback(
    async (id: string, owner: string, comment: Comment) =>
      addComment(await getAxios(), id, owner, comment),
    [getAxios]
  )

  const updateCommentById = useCallback(
    async (
      id: string,
      owner: string,
      commentId: string,
      updates: Partial<Comment>
    ) => updateCommentApi(await getAxios(), id, owner, commentId, updates),
    [getAxios]
  )

  const deleteCommentById = useCallback(
    async (
      id: string,
      owner: string,
      commentId: string,
      deletionData: DeleteCommentData
    ) => deleteCommentApi(await getAxios(), id, owner, commentId, deletionData),
    [getAxios]
  )

  const resolveCommentById = useCallback(
    async (
      id: string,
      owner: string,
      commentId: string,
      resolvedBy: string,
      resolvedByName: string
    ) =>
      resolveCommentApi(
        await getAxios(),
        id,
        owner,
        commentId,
        resolvedBy,
        resolvedByName
      ),
    [getAxios]
  )

  const addReplyById = useCallback(
    async (id: string, owner: string, commentId: string, reply: CommentReply) =>
      addReplyApi(await getAxios(), id, owner, commentId, reply),
    [getAxios]
  )

  const deleteReplyById = useCallback(
    async (id: string, owner: string, commentId: string, replyId: string) =>
      deleteReplyApi(await getAxios(), id, owner, commentId, replyId),
    [getAxios]
  )

  return {
    getCommentsById,
    addCommentById,
    updateCommentById,
    deleteCommentById,
    resolveCommentById,
    addReplyById,
    deleteReplyById,
  }
}
